<template>
  <activity-item-wrapper
    :type="CommunicationType.call"
    :actions
    :status="item.status"
  >
    <template #content>
      <call-disposition-selector
        :log-data="item"
        :version="2"
        :show-call-text="showCallText"
        :teleport-options="{ enabled: true }"
      />
      <lazy-load :options="lazyLoadOptions" persist class="call-recording">
        <call-log-recording
          v-if="item.recording"
          :id="item.id"
          :session-id="item.id"
          :recording="item.recording"
          :teleport-options="{ enabled: true }"
        />
      </lazy-load>
      <lazy-load :options="lazyLoadOptions" persist>
        <call-recording-text
          v-if="showCallText || hasTextData"
          :call-disposition="item.call_disposition"
          is-deal-view
          :call-log="{ type: CommunicationType.call, id, data: item }"
        />
      </lazy-load>
      <call-footer :item />
      <log-comments-section
        v-if="showComments"
        :comments="item.comments ?? []"
        :version="2"
      />
    </template>
  </activity-item-wrapper>
</template>
<script setup lang="ts">
import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";

import { CommunicationType } from "@/enums/communicationLogs";
import { LogEndpointType } from "@/enums/communicationLogs";
import { CommunicationLogContextKey } from "@/helpers/symbols";

import LazyLoad from "@/components/LazyLoad.vue";
import LogCommentsSection from "@/components/communicationLogs/comments/LogCommentsSection.vue";
import LogCommentsToggle from "@/components/communicationLogs/comments/LogCommentsToggle.vue";
import CallDispositionSelector from "@/components/communicationLogs/communication/CallDispositionSelector.vue";
import CallLogRecording from "@/components/communicationLogs/communication/CallLogRecording.vue";
import CallRecordingText from "@/components/communicationLogs/communication/CallRecordingText.vue";
import ActivityItemWrapper from "@/components/deals/dealActivity/ActivityItemWrapper.vue";
import CallFooter from "@/components/deals/dealActivity/call/CallFooter.vue";
import IconMinus from "@/components/icons/IconMinus.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";

import type { LazyLoadOptions } from "@/models/common";
import type { CallLog, CallLogData } from "@/models/communicationLogs";
import type { ActivityAction } from "@/models/dealActivity";

const { item, id } = defineProps<{
  item: CallLogData;
  id: CallLog["id"];
}>();

provide(CommunicationLogContextKey, {
  type: LogEndpointType.CallSession,
  logId: item.id,
});

const lazyLoadOptions: LazyLoadOptions = {
  style: {},
};

const { t } = useI18n();

const showCallText = ref(false);
const showComments = ref(false);

const textVisibilityToggleTooltip = computed(() => {
  return showCallText.value
    ? `${t("COMMON.CLOSE")} ${t("COMMON.SUMMARY")}`
    : `${t("COMMON.SHOW")} ${t("COMMON.SUMMARY")}`;
});

const actions = computed(() => {
  const options: ActivityAction[] = [
    {
      name: "comments",
      component: LogCommentsToggle,
      props: {
        count: item.comments?.length ?? 0,
        modelValue: showComments.value,
      },
      tooltip: showComments.value
        ? `${t("COMMON.HIDE")} ${t("ACTIVITY.COMMENTS.TITLE", 2)}`
        : `${t("COMMON.SEE")} ${t("ACTIVITY.COMMENTS.TITLE", 2)}`,
      click: () => {
        showComments.value = !showComments.value;
      },
    },
  ];

  if (!hasTextData.value) {
    options.unshift({
      name: "text",
      icon: showCallText.value ? IconMinus : IconPlusSmall,
      class: "mt-1 cursor-pointer rounded text-label hover:bg-grey-hover",
      tooltip: textVisibilityToggleTooltip.value,
      click: () => {
        showCallText.value = !showCallText.value;
      },
    });
  }

  return options;
});

const hasTextData = computed(() => {
  const { body, has_summary, has_transcription } = item;

  return !!body || !!has_summary || !!has_transcription;
});
</script>
